import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { AdminNotification, AdminNotificationType } from '../../../types';
import { formatRelativeTime } from '../formatRelativeTime';

function notificationIcon(type: AdminNotificationType) {
  switch (type) {
    case 'booking_created':
      return <EventNoteRoundedIcon fontSize="small" color="primary" />;
    case 'booking_confirmed':
      return <EventAvailableRoundedIcon fontSize="small" color="success" />;
    case 'booking_cancelled':
      return <EventBusyRoundedIcon fontSize="small" color="error" />;
    case 'payment_received':
      return <PaymentsRoundedIcon fontSize="small" color="secondary" />;
    default:
      return <NotificationsNoneRoundedIcon fontSize="small" />;
  }
}

interface Props {
  notification: AdminNotification;
  onOpen: (notification: AdminNotification) => void;
  onDelete?: (notification: AdminNotification) => void;
  showStatusChip?: boolean;
  compact?: boolean;
}

export default function NotificationListItem({
  notification,
  onOpen,
  onDelete,
  showStatusChip = false,
  compact = false,
}: Props) {
  const unread = !notification.read;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 1,
        borderLeft: unread ? 3 : 0,
        borderColor: unread ? 'primary.main' : 'transparent',
        bgcolor: unread ? 'action.hover' : 'transparent',
        opacity: unread ? 1 : 0.82,
        '&:hover': { opacity: 1 },
      }}
    >
      <ListItemButton
        onClick={() => onOpen(notification)}
        sx={{
          flex: 1,
          alignItems: 'flex-start',
          gap: 1,
          pl: 2,
          pr: onDelete ? 0.5 : 1.5,
          py: compact ? 1 : 1.25,
          borderRadius: 1,
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: unread ? 'action.selected' : 'action.hover',
          },
        }}
      >
        <Box sx={{ mt: 0.5 }}>{notificationIcon(notification.type)}</Box>
        <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: unread ? 700 : 500 }}>
              {notification.title}
            </Typography>
            {showStatusChip && (
              <Chip
                label={unread ? 'Unread' : 'Read'}
                size="small"
                color={unread ? 'primary' : 'default'}
                variant={unread ? 'filled' : 'outlined'}
                sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
              />
            )}
          </Box>
        }
        secondary={
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: showStatusChip ? 0.25 : 0 }}
            >
              {notification.message}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {formatRelativeTime(notification.createdAt)}
            </Typography>
          </>
        }
        />
      </ListItemButton>
      {onDelete && (
        <IconButton
          aria-label="Delete notification"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification);
          }}
          sx={{ alignSelf: 'center', mr: 1, flexShrink: 0, color: 'text.secondary' }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
